import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Video, Download, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMedia } from '@/hooks/useApi';

export default function MediaPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'photo' | 'video'>('photo');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { data: media, isLoading } = useMedia(activeTab);

  const tabs = [
    { key: 'photo' as const, label: t('media.photos'), icon: Image },
    { key: 'video' as const, label: t('media.videos'), icon: Video },
  ];

  const openLightbox = (idx: number) => setLightboxIndex(idx);
  const closeLightbox = () => setLightboxIndex(null);
  const prevPhoto = () => {
    if (lightboxIndex !== null && media) {
      setLightboxIndex(lightboxIndex > 0 ? lightboxIndex - 1 : media.length - 1);
    }
  };
  const nextPhoto = () => {
    if (lightboxIndex !== null && media) {
      setLightboxIndex(lightboxIndex < media.length - 1 ? lightboxIndex + 1 : 0);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="section-title">{t('media.title')}</h1>
      <p className="section-subtitle">{t('media.subtitle')}</p>

      {/* Tabs */}
      <div className="flex gap-2 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-theme font-medium text-sm transition-colors ${
              activeTab === tab.key
                ? 'bg-primary text-white'
                : 'bg-bg-secondary text-text-secondary hover:bg-bg-card'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="aspect-square bg-bg-secondary rounded-theme animate-pulse" />
          ))}
        </div>
      ) : Array.isArray(media) && media.length > 0 ? (
        <>
          {activeTab === 'photo' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {media.map((item, idx) => (
                <button
                  key={item.id}
                  onClick={() => openLightbox(idx)}
                  className="group relative aspect-square overflow-hidden rounded-theme bg-bg-secondary"
                >
                  <img
                    src={item.thumbnailUrl}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end p-2">
                    <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity truncate">
                      {item.title}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {media.map((item) => (
                <div key={item.id} className="card">
                  <div className="aspect-video bg-bg-secondary rounded-md overflow-hidden mb-3">
                    <video
                      src={item.url}
                      poster={item.thumbnailUrl}
                      controls
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-medium">{item.title}</h3>
                  <p className="text-xs text-text-secondary mt-1">{item.createdAt}</p>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20 text-text-secondary">{t('common.noData')}</div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && media && media[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[80vh] p-4">
            <img
              src={media[lightboxIndex].url}
              alt={media[lightboxIndex].title}
              className="max-w-full max-h-[70vh] object-contain mx-auto"
            />
            <div className="flex items-center justify-between mt-4 text-white">
              <span className="text-sm">{media[lightboxIndex].title}</span>
              <a
                href={media[lightboxIndex].url}
                download
                className="flex items-center gap-1 text-sm hover:text-primary-light"
              >
                <Download className="w-4 h-4" /> {t('common.download')}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
